import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";

interface HoldButtonProps {
  onConfirm: () => void;
  holdMs?: number;
  idleLabel: string;
  holdingLabelPrefix?: string;
  doneLabel?: string;
  done?: boolean;
  disabled?: boolean;
}

/**
 * Hold-to-confirm button for the panic-close action — deliberately NOT a
 * click+modal. Mirrors the prototype's holdStart/holdEnd/holdLeave handlers:
 * press and hold for `holdMs` (~900-1000ms per the plan) with a visual fill
 * sweeping left-to-right as progress feedback; releasing early cancels and
 * resets to 0. This is the anti-fat-finger guard for an irreversible action
 * on real trading capital, so it's implemented as press-and-hold, not a
 * dialog the user can reflexively click through.
 *
 * Mobile touch required two fixes beyond the mouse/desktop implementation,
 * both confirmed against a real device (not just emulated touch, which
 * doesn't reproduce native gesture-recognizer behavior at all):
 *
 * 1. pointerleave fires immediately after pointerdown on touch — before
 *    pointerup, before any real finger movement — so it's only treated as
 *    a "released without confirming" signal for pointerType === "mouse"
 *    (see handleLeave). For touch, only pointerup/pointercancel end a hold.
 * 2. Mobile browsers run their own native long-press gesture recognizer
 *    (the one behind text-selection/context-menu) as a system watching the
 *    raw touch stream in parallel with whatever JS does with the resulting
 *    Pointer Events. React's synthetic pointer-event preventDefault() does
 *    not reach that layer — only a genuinely non-passive native touchstart/
 *    touchmove listener (registered via addEventListener, not JSX props,
 *    since touch listeners are passive-by-default per the DOM spec) stops
 *    it at the source. See the useEffect below.
 */
export function HoldButton({
  onConfirm,
  holdMs = 900,
  idleLabel,
  holdingLabelPrefix = "УДЕРЖИВАЙТЕ… ",
  doneLabel,
  done = false,
  disabled = false,
}: HoldButtonProps) {
  const [percent, setPercent] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    const stopNativeGesture = (e: TouchEvent) => e.preventDefault();
    el.addEventListener("touchstart", stopNativeGesture, { passive: false });
    el.addEventListener("touchmove", stopNativeGesture, { passive: false });
    return () => {
      el.removeEventListener("touchstart", stopNativeGesture);
      el.removeEventListener("touchmove", stopNativeGesture);
    };
  }, []);

  const cancel = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startRef.current = null;
    if (!firedRef.current) setPercent(0);
  }, []);

  const end = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (pointerIdRef.current !== null) {
        try {
          e.currentTarget.releasePointerCapture(pointerIdRef.current);
        } catch {
          // already released / never captured — fine, nothing to undo
        }
        pointerIdRef.current = null;
      }
      cancel();
    },
    [cancel],
  );

  const handleLeave = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === "mouse") cancel();
    },
    [cancel],
  );

  const tick = useCallback(() => {
    if (startRef.current === null) return;
    const elapsed = performance.now() - startRef.current;
    const pct = Math.min(100, (elapsed / holdMs) * 100);
    setPercent(pct);
    if (pct >= 100) {
      firedRef.current = true;
      startRef.current = null;
      onConfirm();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [holdMs, onConfirm]);

  const start = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      // Text-selection / callout suppression at the source (see class doc).
      e.preventDefault();
      if (disabled || done || firedRef.current) return;
      // Pointer capture ties ALL subsequent events for this pointer (move/
      // up/cancel) to THIS element regardless of where the finger drifts.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
        pointerIdRef.current = e.pointerId;
      } catch {
        // Pointer Capture unsupported (very old browser) — falls back to
        // the pre-capture behavior, still functional on non-flaky tracking.
      }
      startRef.current = performance.now();
      rafRef.current = requestAnimationFrame(tick);
    },
    [disabled, done, tick],
  );

  useEffect(() => cancel, [cancel]);

  // Reset for a fresh hold once the parent clears `done` (e.g. after the
  // confirmation banner times out) — otherwise the button stays "used up".
  useEffect(() => {
    if (!done) {
      firedRef.current = false;
      setPercent(0);
    }
  }, [done]);

  const label = done ? (doneLabel ?? idleLabel) : percent > 0 ? `${holdingLabelPrefix}${Math.round(percent)}%` : idleLabel;

  const filled = done ? 100 : percent;
  const PAD = 16;
  // Genuinely inactive (nothing to close) reads as neutral grey, not a
  // faded red — a disabled-but-still-red button looked like a live control
  // that just wasn't responding, not like a control that's off. `done`
  // (successfully fired) keeps the solid-red success look even though the
  // button is also `disabled` at that point — those are different states
  // that shouldn't share styling.
  const isInactive = disabled && !done;
  const trackBorder = isInactive ? "var(--line2)" : "var(--short)";
  const trackBg = isInactive ? "var(--surface2)" : "var(--shortsoft)";
  const trackText = isInactive ? "var(--fg3)" : "var(--short)";
  // Idle track is a soft/muted red so the solid-red fill sweeping across it
  // reads as a clear, high-contrast left-to-right progress bar — not just a
  // faint tint shift on an already-red button. Confirmed on a real mobile
  // device that the previous version (dark low-alpha overlay on an
  // already-red background) was effectively invisible.
  //
  // Text is rendered TWICE, stacked: a base copy in --short (readable
  // against the idle --shortsoft track) and a second copy in --onfill
  // clipped to exactly the fill width (readable against the solid --short
  // fill once it sweeps under it). This keeps the label in one fixed
  // position and correctly contrasted throughout the sweep, instead of
  // trying to fake it with a single blended color.
  return (
    <button
      ref={buttonRef}
      type="button"
      onPointerDown={start}
      onPointerUp={end}
      onPointerLeave={handleLeave}
      onPointerCancel={end}
      disabled={disabled || done}
      style={{
        position: "relative",
        width: "100%",
        padding: PAD,
        border: `1px solid ${trackBorder}`,
        borderRadius: 12,
        background: trackBg,
        color: trackText,
        cursor: disabled || done ? "default" : "pointer",
        overflow: "hidden",
        font: "700 14px 'Golos Text',sans-serif",
        letterSpacing: ".05em",
        touchAction: "none",
        opacity: isInactive ? 0.6 : 1,
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span style={{ position: "relative", whiteSpace: "nowrap", userSelect: "none", WebkitUserSelect: "none" }}>{label}</span>
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${filled}%`,
          background: "var(--short)",
          overflow: "hidden",
          transition: percent === 0 ? "width 160ms var(--ease)" : "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: PAD,
            top: "50%",
            transform: "translateY(-50%)",
            whiteSpace: "nowrap",
            color: "var(--onfill)",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          {label}
        </span>
      </span>
    </button>
  );
}
